/**
 * Global Toast Context
 *
 * Provides toast notification functionality throughout the app.
 * Listens to toast events from non-React code (e.g., Axios interceptors).
 *
 * Usage in components:
 *   const { showToast } = useToast();
 *   showToast({ type: 'success', title: 'Saved!', message: 'Your changes were saved.' });
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { JSX } from "react";
import Toast from "../components/Toast";
import type { ToastType } from "../components/Toast";
import { subscribeToToasts, type ToastEvent } from "../utils/toastEvents";

interface ToastState {
  show: boolean;
  type: ToastType;
  title: string;
  message?: string;
  duration: number;
}

interface ToastContextType {
  showToast: (event: ToastEvent) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

const DEFAULT_DURATION = 4000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}): JSX.Element => {
  const [toast, setToast] = useState<ToastState>({
    show: false,
    type: "info",
    title: "",
    message: undefined,
    duration: DEFAULT_DURATION,
  });

  const showToast = useCallback((event: ToastEvent): void => {
    setToast({
      show: true,
      type: event.type,
      title: event.title,
      message: event.message,
      duration: event.duration ?? DEFAULT_DURATION,
    });
  }, []);

  const hideToast = useCallback((): void => {
    setToast((prev) => ({ ...prev, show: false }));
  }, []);

  // Subscribe to toast events from non-React code (e.g., Axios interceptors)
  useEffect(() => {
    const unsubscribe = subscribeToToasts(showToast);
    return unsubscribe;
  }, [showToast]);

  const value: ToastContextType = {
    showToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />
    </ToastContext.Provider>
  );
};
